import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useNotificationStore } from '@hooks/useStores';
import NotificationService from '@service/NotificationService';
import { BellIcon } from '@components/common/ui/icons';
import * as styles from './NotificationPanel.module.css';

function formatRelativeTime(dateStr: string, t: (key: string, opts?: any) => string): string {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60_000);
    if (mins < 1) return t('notifications.justNow');
    if (mins < 60) return t('notifications.minAgo', { count: mins });
    const hours = Math.floor(mins / 60);
    if (hours < 24) return t('notifications.hourAgo', { count: hours });
    const days = Math.floor(hours / 24);
    return t('notifications.dayAgo', { count: days });
}

function notificationText(
    n: { type: string; data: { actorLogin: string; noteTitle: string; permission?: string } },
    t: (key: string, opts?: any) => string,
) {
    if (n.type === 'note_shared') {
        const perm = n.data.permission === 'edit' ? t('notifications.permEdit') : t('notifications.permView');
        return t('notifications.noteShared', {
            actor: n.data.actorLogin || t('notifications.someone'),
            title: n.data.noteTitle || t('notifications.aNote'),
            perm,
        });
    }
    if (n.type === 'access_revoked') {
        return t('notifications.accessRevoked', {
            actor: n.data.actorLogin || t('notifications.someone'),
            title: n.data.noteTitle || t('notifications.aNote'),
        });
    }
    if (n.type === 'note_published') {
        return t('notifications.notePublished', {
            actor: n.data.actorLogin || t('notifications.someone'),
            title: n.data.noteTitle || t('notifications.untitled'),
        });
    }
    return t('notifications.newNotification');
}

interface Props {
    onClose: () => void;
    triggerRef?: React.RefObject<HTMLElement | null>;
}

export const NotificationPanel: React.FC<Props> = observer(({ onClose, triggerRef }) => {
    const { t } = useTranslation();
    const notificationStore = useNotificationStore();
    const navigate = useNavigate();
    const panelRef = useRef<HTMLDivElement>(null);
    const [placement, setPlacement] = useState<'top' | 'bottom'>('bottom');

    useEffect(() => {
        if (triggerRef?.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setPlacement(window.innerHeight - rect.bottom < 460 ? 'top' : 'bottom');
        }
    }, []);

    useEffect(() => {
        const handler = (e: Event) => {
            const target = e.target as Node;
            if (
                panelRef.current && !panelRef.current.contains(target) &&
                !(triggerRef?.current && triggerRef.current.contains(target))
            ) {
                onClose();
            }
        };
        document.addEventListener('pointerdown', handler);
        return () => document.removeEventListener('pointerdown', handler);
    }, [onClose, triggerRef]);

    const handleClick = async (id: string, noteId: string, isRead: boolean) => {
        if (!isRead) {
            notificationStore.markRead(id);
            try { await NotificationService.markRead(id); } catch { /* ignore */ }
        }
        onClose();
        navigate(`/note/${noteId}`);
    };

    const handleDelete = async (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        notificationStore.removeNotification(id);
        try { await NotificationService.delete(id); } catch { /* ignore */ }
    };

    const handleMarkAllRead = async () => {
        notificationStore.markAllRead();
        try { await NotificationService.markAllRead(); } catch { /* ignore */ }
    };

    const { notifications } = notificationStore;

    return (
        <div className={`${styles.panel}${placement === 'top' ? ` ${styles.panelUp}` : ''}`} ref={panelRef}>
            <div className={styles.header}>
                <span className={styles.title}>{t('notifications.title')}</span>
                {notifications.some(n => !n.isRead) && (
                    <button className={styles.markAllBtn} onClick={handleMarkAllRead}>
                        {t('notifications.markAllRead')}
                    </button>
                )}
            </div>

            <div className={styles.list}>
                {notifications.length === 0 ? (
                    <div className={styles.empty}>
                        <BellIcon className={styles.emptyIcon} />
                        <p style={{ margin: '6px 0 2px', fontWeight: 500 }}>{t('notifications.emptyTitle')}</p>
                        <p style={{ margin: 0, fontSize: 12, opacity: 0.6 }}>{t('notifications.emptyText')}</p>
                    </div>
                ) : (
                    notifications.map(n => (
                        <div
                            key={n._id}
                            className={`${styles.item} ${!n.isRead ? styles.unread : ''}`}
                            onClick={() => handleClick(n._id, n.data.noteId, n.isRead)}
                        >
                            <div className={styles.itemBody}>
                                <span className={styles.itemText}>{notificationText(n, t)}</span>
                                <span className={styles.itemTime}>{formatRelativeTime(n.createdAt, t)}</span>
                            </div>
                            <button
                                className={styles.deleteBtn}
                                onClick={(e) => handleDelete(e, n._id)}
                                title={t('notifications.dismiss')}
                            >
                                ×
                            </button>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
});
